import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { computeRoi, parseNumber, clamp } from "@/lib/roi";

type Lang = "de" | "en";

const copy = {
  de: {
    locale: "de-DE",
    yourNumbers: "Ihre Zahlen",
    prefilled: "Beispielwerte sind vorbelegt, überschreiben Sie sie mit Ihren echten Daten.",
    now: "Aktuell",
    scenario: "Angenommenes Szenario",
    ordersMonth: "Bestellungen / Monat",
    diffMonth: "Umsatzdifferenz / Monat",
    diffYearLabel: "Umsatzdifferenz / Jahr",
    extraOrders: "Bestellungen / Monat mehr",
    revenueChart: "Umsatz pro Monat, aktuell und Szenario",
    revenue: "Umsatz",
    cumulative: "Kumulierter Umsatz über 12 Monate",
    diffYear: "Differenz nach einem Jahr:",
    disclaimer:
      "Rechenbeispiel, keine Prognose. Umsatz ist nicht Gewinn; Marge, Kosten und weitere Einflussfaktoren sind nicht berücksichtigt.",
    fields: [
      {
        id: "traffic",
        label: "Sessions pro Monat",
        suffix: "Sessions",
        hint: "Tatsächlicher Monatswert aus Ihrer Statistik, zum Beispiel 25000.",
      },
      {
        id: "crNow",
        label: "Conversion-Rate heute",
        suffix: "%",
        hint: "Ihr aktueller Wert aus Shopify Analytics.",
      },
      {
        id: "aov",
        label: "Durchschnittlicher Warenkorb",
        suffix: "€",
        hint: "Netto-Bestellwert inklusive Upsells.",
      },
      {
        id: "crScenario",
        label: "Angenommene Conversion-Rate",
        suffix: "%",
        hint: "Frei wählbarer Annahmewert, kein zugesagtes Ergebnis.",
      },
    ],
  },
  en: {
    locale: "en-GB",
    yourNumbers: "Your numbers",
    prefilled: "Example values are pre-filled, replace them with your real data.",
    now: "Today",
    scenario: "Assumed scenario",
    ordersMonth: "orders / month",
    diffMonth: "Revenue difference / month",
    diffYearLabel: "Revenue difference / year",
    extraOrders: "additional orders / month",
    revenueChart: "Monthly revenue, today and scenario",
    revenue: "Revenue",
    cumulative: "Cumulative revenue over 12 months",
    diffYear: "Difference after one year:",
    disclaimer:
      "Worked example, not a forecast. Revenue is not profit; margin, costs and other factors are not included.",
    fields: [
      {
        id: "traffic",
        label: "Sessions per month",
        suffix: "sessions",
        hint: "Actual monthly figure from your analytics, for example 25000.",
      },
      {
        id: "crNow",
        label: "Conversion rate today",
        suffix: "%",
        hint: "Your current value from Shopify Analytics.",
      },
      {
        id: "aov",
        label: "Average order value",
        suffix: "€",
        hint: "Net order value including upsells.",
      },
      {
        id: "crScenario",
        label: "Assumed conversion rate",
        suffix: "%",
        hint: "A freely chosen assumption, not a promised result.",
      },
    ],
  },
} as const;

type FieldId = "traffic" | "crNow" | "aov" | "crScenario";

const fieldMeta: Record<FieldId, { min: number; max: number }> = {
  traffic: { min: 0, max: 100_000_000 },
  crNow: { min: 0, max: 100 },
  aov: { min: 0, max: 1_000_000 },
  crScenario: { min: 0, max: 100 },
};

const defaults: Record<FieldId, string> = {
  traffic: "25000",
  crNow: "1,4",
  aov: "78",
  crScenario: "2,1",
};

export function RoiCalculator({ lang = "de" }: { lang?: Lang }) {
  const t = copy[lang];
  const eur = (v: number) =>
    new Intl.NumberFormat(t.locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(v);

  const [raw, setRaw] = useState<Record<FieldId, string>>(
    lang === "de"
      ? defaults
      : { traffic: "25000", crNow: "1.4", aov: "78", crScenario: "2.1" },
  );

  const values = useMemo(() => {
    const read = (id: FieldId) =>
      clamp(parseNumber(raw[id]), fieldMeta[id].min, fieldMeta[id].max);
    return {
      traffic: read("traffic"),
      crNow: read("crNow"),
      aov: read("aov"),
      crScenario: read("crScenario"),
    };
  }, [raw]);

  const r = useMemo(() => computeRoi(values), [values]);

  const positive = r.deltaMonth > 0;
  const negative = r.deltaMonth < 0;
  const deltaColor = positive
    ? "text-[var(--color-chart-4)]"
    : negative
      ? "text-destructive"
      : "text-foreground";

  const compareData = [
    { name: t.now, value: Math.round(r.revenueNow) },
    { name: t.scenario, value: Math.round(r.revenueScenario) },
  ];

  const cumulative = Array.from({ length: 12 }, (_, i) => ({
    monat: `M${i + 1}`,
    now: Math.round(r.revenueNow * (i + 1)),
    scenario: Math.round(r.revenueScenario * (i + 1)),
  }));

  const axis = {
    stroke: "var(--color-muted-foreground)",
    fontSize: 11,
    tickLine: false,
    axisLine: false,
  } as const;

  const tooltipStyle = {
    background: "var(--color-card)",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    fontSize: 12,
    color: "var(--color-foreground)",
  } as const;

  return (
    <div className="shadow-lift overflow-hidden rounded-xl border border-border bg-card">
      <div className="grid gap-8 p-6 md:grid-cols-[320px_1fr] md:p-8">
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold">{t.yourNumbers}</h3>
            <p className="mt-1 text-xs text-muted-foreground">{t.prefilled}</p>
          </div>

          {t.fields.map((f) => {
            const id = f.id as FieldId;
            return (
              <div key={f.id} className="grid gap-1.5">
                <label
                  htmlFor={f.id}
                  className="text-xs font-semibold uppercase tracking-[0.12em] text-foreground/90"
                >
                  {f.label}
                </label>
                <div className="flex items-center gap-2 rounded-lg border-2 border-input bg-background/80 px-3 shadow-sm transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30 hover:border-primary/50">
                  <input
                    id={f.id}
                    type="text"
                    inputMode="decimal"
                    value={raw[id]}
                    onChange={(e) => setRaw((v) => ({ ...v, [id]: e.target.value }))}
                    className="w-full bg-transparent py-3 font-display text-lg font-semibold tabular-nums text-foreground outline-none"
                  />
                  <span className="shrink-0 text-xs font-medium text-primary">{f.suffix}</span>
                </div>
                <span className="text-[11px] leading-snug text-muted-foreground/80">{f.hint}</span>
              </div>
            );
          })}
        </div>

        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-background p-5">
              <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-chart-5)]">
                {t.now}
              </p>
              <p className="mt-2 font-display text-3xl font-bold text-[var(--color-chart-5)]">
                {eur(r.revenueNow)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {Math.round(r.ordersNow).toLocaleString(t.locale)} {t.ordersMonth}
              </p>
            </div>
            <div className="rounded-lg border border-primary/40 bg-background p-5">
              <p className="text-xs uppercase tracking-[0.18em] text-primary">{t.scenario}</p>
              <p className="mt-2 font-display text-3xl font-bold text-signal">
                {eur(r.revenueScenario)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {Math.round(r.ordersScenario).toLocaleString(t.locale)} {t.ordersMonth}
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { k: t.diffMonth, v: eur(r.deltaMonth), accent: true },
              { k: t.diffYearLabel, v: eur(r.deltaYear), accent: true },
              {
                k: t.extraOrders,
                v: Math.round(r.extraOrders).toLocaleString(t.locale),
                accent: false,
              },
            ].map((s) => (
              <div key={s.k} className="rounded-lg border border-border bg-background p-4">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{s.k}</p>
                <p
                  className={`mt-1.5 font-display text-xl font-bold ${
                    s.accent ? deltaColor : "text-foreground"
                  }`}
                >
                  {s.v}
                </p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-border bg-background p-5">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {t.revenueChart}
            </p>
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compareData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border)"
                    vertical={false}
                  />
                  <XAxis dataKey="name" {...axis} />
                  <YAxis
                    {...axis}
                    width={70}
                    tickFormatter={(v: number) => `${Math.round(v / 1000)}k €`}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    cursor={{ fill: "var(--color-muted)", opacity: 0.25 }}
                    formatter={(v: number) => [eur(v), t.revenue]}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={90}>
                    {compareData.map((entry, i) => (
                      <Cell
                        key={entry.name}
                        fill={i === 0 ? "var(--color-chart-5)" : "var(--color-chart-4)"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            {lang === "de" ? (
              <>
                Rechnung: Sessions × Conversion-Rate × Warenkorb, einmal mit Ihrem heutigen Wert
                und einmal mit Ihrem Annahmewert. {t.disclaimer}
              </>
            ) : (
              <>
                Maths: sessions × conversion rate × average order value, once with your current
                value and once with your assumption. {t.disclaimer}
              </>
            )}
          </p>
        </div>
      </div>

      <div className="border-t border-border px-6 pb-6 pt-6 md:px-8 md:pb-8">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {t.cumulative}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t.diffYear} <span className={`font-semibold ${deltaColor}`}>{eur(r.deltaYear)}</span>
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="h-1.5 w-4 rounded-full bg-[var(--color-chart-5)]" />
              {t.now} {eur(cumulative[cumulative.length - 1]?.now ?? 0)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-4 rounded-full bg-[var(--color-chart-4)]" />
              <span className="font-semibold text-[var(--color-chart-4)]">
                {t.scenario} {eur(cumulative[cumulative.length - 1]?.scenario ?? 0)}
              </span>
            </span>
          </div>
        </div>

        <div className="mt-4 h-56 md:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={cumulative} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="roiAfterFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-chart-4)" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="var(--color-chart-4)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="roiBeforeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-chart-5)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="var(--color-chart-5)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis dataKey="monat" {...axis} interval={0} />
              <YAxis
                {...axis}
                width={70}
                tickFormatter={(v: number) => `${Math.round(v / 1000)}k €`}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: number, n) => [eur(v), n === "now" ? t.now : t.scenario]}
              />
              <Area
                type="monotone"
                dataKey="now"
                stroke="var(--color-chart-5)"
                strokeWidth={2}
                fill="url(#roiBeforeFill)"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="scenario"
                stroke="var(--color-chart-4)"
                strokeWidth={2.5}
                fill="url(#roiAfterFill)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">{t.disclaimer}</p>
      </div>
    </div>
  );
}
